/**
 * /admin/users (BUILD_SPEC.md §6.4, §10.6).
 *
 * Surfaces all the user-management mutations the M3 addendum API
 * exposes: create EMPLOYEE, toggle canEdit/isActive, reset password,
 * delete. UI guards (`canDeleteUser`, `canDeactivateUser`) hide buttons
 * that would hit the server-side ADMIN_PROTECTED / SELF_DELETE_FORBIDDEN
 * / SELF_DEACTIVATE_FORBIDDEN paths — the server is still the source of
 * truth, but the UI shouldn't tempt the user to click into a 403.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { KeyRound, Trash2, Power, Copy, Check } from "lucide-react";
import {
  canDeactivateUser,
  canDeleteUser,
  createUser,
  deleteUser,
  listUsers,
  resetUserPassword,
  updateUser,
} from "@/api/users";
import type { User } from "@/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuth } from "@/hooks/useAuth";

/**
 * v1.1 B3: role-aware create payload. Admins pick from three options in
 * the UI — EMPLOYEE, EMPLOYEE with canEdit, or DISPLAY (TV kiosk). The
 * schema uses a discriminated union on `role` so the validator enforces
 * "EMPLOYEE requires password" and "DISPLAY accepts neither password
 * nor canEdit" at form-submit time, mirroring the server's
 * `CreateUserSchema` refine in `apps/api/src/routes/users.ts`.
 */
const usernameField = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9._-]+$/u);
const CreateSchema = z.discriminatedUnion("role", [
  z.object({
    role: z.literal("EMPLOYEE"),
    username: usernameField,
    password: z.string().min(12).max(256),
    canEdit: z.boolean().optional(),
  }),
  z.object({
    role: z.literal("DISPLAY"),
    username: usernameField,
  }),
]);
type CreateValues = z.infer<typeof CreateSchema>;

/** Captured from the 201 response when a DISPLAY user is created. The
 *  `initialPassword` is shown once and never persisted client-side. */
interface DisplayCredentials {
  username: string;
  password: string;
}

const ResetSchema = z.object({
  newPassword: z.string().min(12).max(256),
});

export function UsersAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [displayCreds, setDisplayCreds] = useState<DisplayCredentials | null>(null);

  const query = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["users"] });

  const createMutation = useMutation({
    mutationFn: (values: CreateValues) => {
      if (values.role === "DISPLAY") {
        return createUser({ role: "DISPLAY", username: values.username });
      }
      const payload: Parameters<typeof createUser>[0] = {
        role: "EMPLOYEE",
        username: values.username,
        password: values.password,
      };
      if (values.canEdit !== undefined) payload.canEdit = values.canEdit;
      return createUser(payload);
    },
    onSuccess: (response) => {
      setCreating(false);
      invalidate();
      // v1.1 B3: DISPLAY creates carry a one-shot plaintext password in
      // the response. Surface it ONCE on the creds screen; after the
      // admin dismisses that dialog there is no other recovery path.
      if (response.initialPassword) {
        setDisplayCreds({
          username: response.user.username,
          password: response.initialPassword,
        });
      }
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { canEdit?: boolean; isActive?: boolean } }) =>
      updateUser(id, patch),
    onSuccess: () => invalidate(),
  });
  const resetMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      resetUserPassword(id, newPassword),
    onSuccess: () => setResetting(null),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => invalidate(),
  });

  const columns: Column<User>[] = [
    {
      key: "username",
      header: t("users.username"),
      render: (r) => (
        <span className="font-medium text-fg dark:text-fg-dark">
          {r.username}
          {me && r.id === me.id ? (
            <span className="ms-2 text-xs text-muted dark:text-muted-dark">({t("common.username").toLowerCase()}: you)</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "role",
      header: t("users.role"),
      render: (r) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
            r.role === "ADMIN"
              ? "bg-pulse/20 text-pulse dark:bg-pulse-dark/20 dark:text-pulse-dark"
              : "bg-hairline/40 dark:bg-hairline-dark/60"
          }`}
        >
          {t(`users.${r.role}`)}
        </span>
      ),
    },
    {
      key: "canEdit",
      header: t("users.canEdit"),
      render: (r) => (
        <input
          type="checkbox"
          checked={r.canEdit}
          // Admin's canEdit is meaningless (always effectively true via
          // role); disable the checkbox to keep the UI honest.
          disabled={r.role === "ADMIN" || updateMutation.isPending}
          onChange={(e) => updateMutation.mutate({ id: r.id, patch: { canEdit: e.target.checked } })}
          className="h-4 w-4"
        />
      ),
      hideOnMobile: true,
    },
    {
      key: "isActive",
      header: t("users.isActive"),
      render: (r) => {
        const blocked = !canDeactivateUser(r, me?.id ?? null);
        return (
          <button
            type="button"
            onClick={() =>
              updateMutation.mutate({ id: r.id, patch: { isActive: !r.isActive } })
            }
            disabled={blocked || updateMutation.isPending}
            title={blocked ? t("users.selfProtected") : ""}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${
              r.isActive
                ? "bg-approval-bg text-approval dark:bg-approval-dark-bg/40 dark:text-approval-dark"
                : "bg-rejection-bg text-rejection dark:bg-rejection-dark-bg/40 dark:text-rejection-dark"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Power className="h-3 w-3" />
            {r.isActive ? t("users.activate").replace("تفعيل", "نشط").replace("Activate", "Active") : t("users.deactivate").replace("تعطيل", "معطّل").replace("Deactivate", "Inactive")}
          </button>
        );
      },
    },
    {
      key: "actions",
      header: t("common.actions"),
      render: (r) => {
        const protectedRow = !canDeleteUser(r, me?.id ?? null);
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setResetting(r)}
              aria-label={t("users.resetPassword")}
              title={t("users.resetPassword")}
              className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setDeleting(r)}
              disabled={protectedRow}
              title={
                protectedRow
                  ? r.role === "ADMIN"
                    ? t("users.adminProtected")
                    : t("users.selfProtected")
                  : t("common.delete")
              }
              aria-label={t("common.delete")}
              className="rounded-md p-1 text-rejection disabled:opacity-30 disabled:cursor-not-allowed hover:bg-rejection-bg dark:text-rejection-dark dark:hover:bg-rejection-dark-bg/40"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("admin.users")}</h2>
      <DataTable
        rows={query.data ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        addButton={{ label: t("users.create"), onClick: () => setCreating(true) }}
        isLoading={query.isLoading}
      />

      <CreateUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={(v) => createMutation.mutate(v)}
        submitting={createMutation.isPending}
        error={createMutation.error}
      />

      <DisplayCredentialsDialog
        creds={displayCreds}
        onClose={() => setDisplayCreds(null)}
      />

      <ResetPasswordDialog
        target={resetting}
        onClose={() => setResetting(null)}
        onSubmit={(newPassword) => {
          if (resetting) resetMutation.mutate({ id: resetting.id, newPassword });
        }}
        submitting={resetMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleting}
        destructive
        title={t("common.delete")}
        body={deleting ? t("users.deleteConfirm") : ""}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (deleting) {
            deleteMutation.mutate(deleting.id);
            setDeleting(null);
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateValues) => void;
  submitting: boolean;
  error: unknown;
}

function CreateUserDialog({ open, onClose, onSubmit, submitting, error }: CreateUserDialogProps) {
  const { t } = useTranslation();
  // v1.1 B3: three preset choices that map to the discriminated-union
  // payload below. "employeeCanEdit" is the same as "employee" with
  // canEdit defaulted on — the spec calls this out as a separate
  // dropdown entry for admin clarity.
  type Preset = "employee" | "employeeCanEdit" | "display";
  const [preset, setPreset] = useState<Preset>("employee");

  // We use a fresh useForm per preset because the resolver picks one
  // variant of the discriminated union depending on `role`. Switching
  // presets resets the form so stale fields don't carry over.
  const { register, handleSubmit, reset, formState } = useForm<CreateValues>({
    resolver: zodResolver(CreateSchema),
    defaultValues:
      preset === "display"
        ? ({ role: "DISPLAY" } as Partial<CreateValues>)
        : ({ role: "EMPLOYEE", canEdit: preset === "employeeCanEdit" } as Partial<CreateValues>),
  });

  if (!open && formState.isSubmitted) reset();
  if (!open) return null;
  const inputCls =
    "w-full rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-fg dark:text-fg-dark";

  const isDisplay = preset === "display";
  const canEditDefault = preset === "employeeCanEdit";

  return (
    <dialog
      open
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="w-[min(28rem,90vw)] p-6 space-y-3">
        <h3 className="text-lg font-bold text-fg dark:text-fg-dark">{t("users.create")}</h3>

        {/* Role / preset selector. Bound to local state because the
            schema variants need a re-validate when the choice changes;
            useForm's resolver reads `values.role` at submit time. The
            hidden `role` input below carries the selection into the
            form so handleSubmit gets the right discriminator. */}
        <label className="block text-sm">
          <span className="block mb-1 text-fg dark:text-fg-dark">{t("users.roleChoice.label")}</span>
          <select
            className={inputCls}
            value={preset}
            onChange={(e) => {
              const next = e.target.value as Preset;
              setPreset(next);
              if (next === "display") {
                reset({ role: "DISPLAY" } as Partial<CreateValues>);
              } else {
                reset({
                  role: "EMPLOYEE",
                  canEdit: next === "employeeCanEdit",
                } as Partial<CreateValues>);
              }
            }}
          >
            <option value="employee">{t("users.roleChoice.employee")}</option>
            <option value="employeeCanEdit">{t("users.roleChoice.employeeCanEdit")}</option>
            <option value="display">{t("users.roleChoice.display")}</option>
          </select>
        </label>

        <input
          type="hidden"
          {...register("role")}
          value={isDisplay ? "DISPLAY" : "EMPLOYEE"}
        />

        <label className="block text-sm">
          <span className="block mb-1 text-fg dark:text-fg-dark">{t("users.username")}</span>
          <input className={inputCls} {...register("username")} autoFocus />
        </label>

        {/* Password + canEdit only when creating an EMPLOYEE. DISPLAY
            accounts get a server-generated password returned in the
            201 response and surfaced by DisplayCredentialsDialog. */}
        {!isDisplay && (
          <>
            <label className="block text-sm">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("users.newPassword")}</span>
              <input
                className={inputCls}
                type="password"
                {...register("password" as never)}
              />
              <span className="mt-1 block text-xs text-muted dark:text-muted-dark">
                {t("users.passwordHint")}
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm text-fg dark:text-fg-dark">
              <input
                type="checkbox"
                defaultChecked={canEditDefault}
                {...register("canEdit" as never)}
              />
              {t("users.canEdit")}
            </label>
          </>
        )}

        {error ? (
          <p className="text-sm text-rejection dark:text-rejection-dark" role="alert">
            {(error as Error).message ?? t("common.errorTitle")}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
          >
            {t("common.create")}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * One-shot credentials dialog (v1.1 B3). Renders right after a DISPLAY
 * account is created. Carries the username + server-generated password
 * with copy buttons and on-screen setup instructions. Dismissing the
 * dialog discards the password from React state; there is no other
 * surface that can recover it, matching the bootstrap-secret model.
 */
function DisplayCredentialsDialog({
  creds,
  onClose,
}: {
  creds: DisplayCredentials | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<"username" | "password" | null>(null);

  if (!creds) return null;

  const copy = (which: "username" | "password", value: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    });
  };

  const fieldRow = (label: string, value: string, which: "username" | "password") => (
    <div className="flex items-center gap-2 rounded-md border border-hairline dark:border-hairline-dark bg-canvas dark:bg-canvas-dark px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted dark:text-muted-dark">{label}</div>
        <div className="rtl-isolate font-mono text-sm text-fg dark:text-fg-dark break-all">
          {value}
        </div>
      </div>
      <button
        type="button"
        onClick={() => copy(which, value)}
        aria-label={
          which === "username" ? t("users.displayCreated.copyUsername") : t("users.displayCreated.copyPassword")
        }
        className="shrink-0 rounded-md p-1.5 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
      >
        {copied === which ? (
          <Check className="h-4 w-4 text-approval dark:text-approval-dark" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );

  return (
    <dialog
      open
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40"
    >
      <div className="w-[min(32rem,92vw)] p-6 space-y-3">
        <h3 className="text-lg font-bold text-fg dark:text-fg-dark">
          {t("users.displayCreated.title")}
        </h3>
        <p className="text-sm text-fg dark:text-fg-dark">{t("users.displayCreated.body")}</p>
        {fieldRow(t("users.username"), creds.username, "username")}
        {fieldRow(t("users.newPassword"), creds.password, "password")}
        <pre className="whitespace-pre-wrap text-xs text-muted dark:text-muted-dark font-sans rounded-md border border-hairline dark:border-hairline-dark p-3 bg-canvas dark:bg-canvas-dark">
          {t("users.displayCreated.instructions")}
        </pre>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold"
          >
            {t("users.displayCreated.done")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

interface ResetPasswordDialogProps {
  target: User | null;
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  submitting: boolean;
}

function ResetPasswordDialog({ target, onClose, onSubmit, submitting }: ResetPasswordDialogProps) {
  const { t } = useTranslation();
  const { register, handleSubmit, formState } = useForm<{ newPassword: string }>({
    resolver: zodResolver(ResetSchema),
  });

  if (!target) return null;
  const inputCls =
    "w-full rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-fg dark:text-fg-dark";

  return (
    <dialog
      open
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40"
    >
      <form
        onSubmit={handleSubmit((v) => onSubmit(v.newPassword))}
        className="w-[min(28rem,90vw)] p-6 space-y-3"
      >
        <h3 className="text-lg font-bold text-fg dark:text-fg-dark">
          {t("users.resetPassword")} — <span className="rtl-isolate">{target.username}</span>
        </h3>
        <label className="block text-sm">
          <span className="block mb-1 text-fg dark:text-fg-dark">{t("users.newPassword")}</span>
          <input className={inputCls} type="password" {...register("newPassword")} autoFocus />
          <span className="mt-1 block text-xs text-muted dark:text-muted-dark">
            {t("users.passwordHint")}
          </span>
          {formState.errors.newPassword ? (
            <span className="mt-1 block text-xs text-rejection dark:text-rejection-dark">
              {formState.errors.newPassword.message}
            </span>
          ) : null}
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </dialog>
  );
}
